export const WEAPON = {
  id: 'lmg',
  label: 'M249-SPW',
  class: 'lmg',
  caliber: '5.56x45',
  /**
   * Premium late-game weapon, priced well above the SMG (800) and the
   * shotgun (1200) already in the armory but below a purpose-built sniper
   * rifle's precision (the M700-LR is 2000): a 150-round belt-substitute
   * box magazine and sustained full-auto fire are a bigger force multiplier
   * than a single high-damage shot, but this is still a support weapon, not
   * the hardest counter-sniper tool in the game.
   */
  price: 2200,
  /* --- fire control ---
   * A real M249's gas regulator gives roughly 725-1000 rpm depending on
   * setting; 780 sits on the low (sustained-fire) setting, which is also
   * the setting that keeps the barrel from cooking on a 150-round mag. No
   * useful burst mode exists on a belt-fed support gun — a gunner either
   * taps for semi-auto precision or holds it down for suppression — so
   * burstCount/burstRpm/burstDelay below are neutral placeholders never
   * reached by `modes`, the same pattern defs/sniper.js uses for its
   * single-shot bolt gun. */
  rpm: 780,
  modes: ['auto', 'semi'],
  burstCount: 1,
  burstRpm: 780,
  burstDelay: 0.1,
  /* --- ammunition ---
   * 150 rounds is the box-magazine compromise this weapon's brief calls
   * for: bigger than any detachable STANAG mag, short of the M249's real
   * 200-round belt box, split across the neck + drum described in
   * models/lmg.js. Reserve is 3 spare mags, the same ratio the carbine's
   * 210/30 = 7 uses scaled to this gun's much bigger single load. */
  magSize: 150,
  reserve: 450,
  /* --- terminal ballistics ---
   * Same 5.56x45 cartridge the M4A1 fires, out of a slightly longer 400 mm
   * SPW barrel (vs. the carbine's 368 mm/14.5"), so velocity is a little
   * higher and damage/penetration/dropoff are carried over unchanged from
   * defs/rifle.js — the cartridge decides terminal ballistics, not the gun
   * wrapped around it. tracerEvery is 5 (4-ball-1-tracer) rather than the
   * carbine's 3: that ratio is the actual belt-linked combat mix a SAW
   * gunner loads, kept here as the one deliberate nod to this weapon's belt
   * heritage even though it now feeds from a box. */
  muzzleVelocity: 895,
  damage: 33,
  penetration: 1.0,
  dropoff: 0.62,
  maxRange: 440,
  dragK: 0.28,
  tracerEvery: 5,
  /* --- accuracy (degrees) ---
   * Heavier and less handy at the hip than a carbine (spreadHip up from the
   * rifle's 2.05), and the red dot doesn't tighten it as much either
   * (spreadAds up from 0.24) — there is no match trigger or free-float
   * precision barrel here. What the mass buys back is sustained fire:
   * spreadPerShot is LOWER than the carbine's 0.3, because a 7 kg gun
   * doesn't wander shot-to-shot the way a 3.4 kg one does, and spreadDecay
   * is slower (3.0 vs 3.6) because that same mass takes longer to settle
   * once the trigger is released. */
  spreadHip: 2.6,
  spreadAds: 0.3,
  spreadPerShot: 0.22,
  spreadMax: 3.6,
  spreadDecay: 3.0,
  /* --- recoil ---
   * The counter-intuitive part: gun mass ROUGHLY HALVES felt recoil for a
   * given cartridge impulse (same F=ma, bigger m), so a 6.9 kg M249 should
   * kick noticeably less than a 3.4 kg M4A1 firing the same 5.56 round.
   * But this is an OPEN-BOLT gun, unlike the carbine's closed bolt: a
   * carrier group nearly as heavy as the whole barrelled action slams
   * forward and locks an instant before every round fires, and that
   * mechanical thump is felt on top of the (smaller) firing impulse. Net
   * result: pitch/yaw/kickBack/kickUp sit slightly ABOVE the carbine's
   * despite the heavier gun. What the mass DOES buy is the recovery —
   * `damping` and `freq` below pull the muzzle back down hard between
   * shots, and `climbShape` actually dips under 1.0 after the first couple
   * of rounds (the carbine's never drops below 1.0), which is the numeric
   * expression of "long, controllable bursts" a real SAW is known for. */
  recoil: {
    pitch: 0.0095,
    yaw: 0.0026,
    kickBack: 0.021,
    kickUp: 0.0078,
    roll: 0.03,
    punch: 0.34,
    freq: 7.5,
    damping: 0.56,
    patternLength: 40,
    patternSeed: 0x249ac1,
    climbShape: [1.3, 1.12, 1.0, 0.92, 0.88],
    drift: 0.4,
  },
  /* --- handling (seconds) ---
   * Slower across the board than the carbine: shouldering ~7 kg takes
   * longer (adsTime 0.42 vs 0.22), swapping a mag this size takes longer
   * (reloadTac/reloadEmpty), and drawing/holstering a gun this size from a
   * sling takes longer too. */
  adsTime: 0.42,
  adsFov: 0.72,
  viewFov: 0.86,
  reloadTac: 3.4,
  reloadEmpty: 4.6,
  inspectTime: 4.0,
  drawTime: 0.85,
  holsterTime: 0.55,
  /* --- pose ---
   * SOLVED FROM THE BORE AXIS, same method as defs/rifle.js — reproduced
   * here, not re-invented, and adapted for a bigger gun rather than
   * independently re-measured against a capture (none was run this
   * session; see the weapon's build report).
   *
   * The convergence/roll angles the rig needs are about the eye-to-weapon
   * geometry, not this gun specifically — rifle.js, smg.js and sniper.js
   * all land within a couple hundredths of a radian of each other on rx
   * (-0.05 exactly, all three) and rz (-0.13 to -0.135), so rx=-0.05 and
   * rz=-0.13 are carried over unchanged, and ry=0.078 matches the sniper's
   * (another two-handed gun long enough that the same convergence angle
   * applies) rather than the carbine's tighter 0.081.
   *
   * hipPos.z is what actually differs per weapon, because it is solved
   * against how much of the gun has to fit in frame: rifle.js uses -0.3 for
   * a 0.747 m gun, sniper.js -0.335 for a 1.22 m one. This gun is shorter
   * than the sniper (~0.95 m crown-to-buttpad) but bulkier — the optic
   * sits 40 mm higher (bore+0.086 vs the carbine's bore+0.067) and the
   * drum magazine hangs lower than any curved STANAG mag — so -0.32 sits
   * between the two, closer to the sniper's because vertical bulk, not
   * barrel length, is the tighter framing constraint here.
   *
   * hipPos.y is lower than the carbine's -0.185: a support gunner carries
   * this weight closer to the body's centre of mass, not held out and up
   * the way a light carbine is. */
  hipPos: [0.115, -0.2, -0.32],
  hipRot: [-0.05, 0.078, -0.13],
  adsCant: [0, 0, 0.004],
  /* Eye to the rear lens. The optic is built with the SAME 31 mm tube and
   * 52 mm length as the rifle's (see models/lmg.js buildOptic call), so the
   * same aperture-budget solve applies unchanged — see defs/rifle.js's
   * eyeRelief comment for the derivation. */
  eyeRelief: 0.115,
  /* Sprint/low-ready: carried over by the same delta from hipPos that
   * rifle.js uses, so the blend doesn't translate the weapon sideways on
   * the way into a sprint (same reasoning as rifle.js's sprintPos comment).
   * sprintRot/lowReadyRot are the shared values every two-handed gun in the
   * game uses (rifle.js and sniper.js are identical on both), because they
   * describe how the RIG carries a slung weapon, not the weapon itself. */
  sprintPos: [0.087, -0.277, -0.295],
  sprintRot: [-0.4, 0.6, 0.2],
  lowReadyPos: [0.109, -0.295, -0.309],
  lowReadyRot: [-0.46, 0.125, -0.09],
  /* More sway than any other gun (1.15 vs the sniper's 1.05) — sheer mass
   * fights the shooter's stance more than a scope's magnification does —
   * and less bob than any other gun (0.8 vs the sniper's 0.85): the same
   * mass that hurts sway damps the vertical bounce of a moving stride. */
  swayScale: 1.15,
  bobScale: 0.8,
  // Matches models/lmg.js's magSize.len (neck + drum diameter) exactly.
  magLen: 0.24,
};

export const loadModel = () => import('../models/lmg.js').then((m) => m.buildLmg);
